import _slicedToArray from "@babel/runtime/helpers/slicedToArray";

/* any functionality that's shared between component variants but not needed by external components goes in this file */
import { useState, useRef } from 'react';
import { getFirstTabbableDescendent, getAllTabbableDescendents, tabbingOutsideOfElement, useLayoutEffectIsomorphic } from '@chegg-ui/utils';

var makeBodyElementsInert = function makeBodyElementsInert(excludeElementSelector) {
  // to avoid unhiding something we shouldn't, we don't include any already hidden elements that may exist or the modal parent container
  var elementsToHide = Array.from(document.querySelectorAll("body > :not([aria-hidden]):not(".concat(excludeElementSelector, ")")));

  for (var i = 0; i < elementsToHide.length; i++) {
    var element = elementsToHide[i];
    element.setAttribute('aria-hidden', 'true');
  }

  return elementsToHide;
};

var resetInertBodyElements = function resetInertBodyElements(elementsMadeInert) {
  for (var i = 0; i < elementsMadeInert.length; i++) {
    var element = elementsMadeInert[i];
    element.removeAttribute('aria-hidden');
  }
};

export function useModalBehavior(_ref) {
  var isOpen = _ref.isOpen,
      onClose = _ref.onClose,
      id = _ref.id,
      selector = _ref.selector;
  var modalRef = useRef(null);

  var _useState = useState(false),
      _useState2 = _slicedToArray(_useState, 2),
      closing = _useState2[0],
      setClosing = _useState2[1];

  var _useState3 = useState([]),
      _useState4 = _slicedToArray(_useState3, 2),
      elementsMadeInert = _useState4[0],
      setElementsMadeInert = _useState4[1];

  var returnFocus = useRef(null);
  var mounted = useRef(false); // all the logic contained within is client-side only in response to user action, thus safe for SSR

  useLayoutEffectIsomorphic(function () {
    if (isOpen) {
      returnFocus.current = document.activeElement;
      var modalElement = modalRef.current; // we need to make all other elements in the DOM body inert, per legacy ARIA spec
      // https://www.w3.org/TR/wai-aria-practices-1.1/examples/dialog-modal/dialog.html

      setElementsMadeInert(makeBodyElementsInert(selector || "#".concat(id, "-container")));
      /* istanbul ignore else */

      if (modalElement) {
        var focusTarget = getFirstTabbableDescendent(modalElement);
        /* istanbul ignore else */

        if (focusTarget) {
          focusTarget.focus();
        }
      } // we don't want to run any of the below logic on the initial mount

    } else if (mounted.current) {
      resetInertBodyElements(elementsMadeInert);

      if (window.matchMedia && !window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
        setClosing(true);
        setTimeout(function () {
          setClosing(false);
          /* istanbul ignore else */

          if (returnFocus.current) {
            returnFocus.current.focus();
          }
        }, // important: timeout number must match the fadeout animation time to keep things smooth
        300);
      } else {
        /* istanbul ignore else */
        if (returnFocus.current) {
          returnFocus.current.focus();
        }
      }
    }

    mounted.current = true;
  }, [isOpen]);

  var onModalKeyDown = function onModalKeyDown(e) {
    var modalElement = modalRef.current; // we specifically only want to ignore the !modalElement case, as that's essentially invalid React behavior

    if (
    /* istanbul ignore next */
    modalElement && tabbingOutsideOfElement(e, modalElement)) {
      e.preventDefault();

      if (!e.shiftKey) {
        var firstChild = getFirstTabbableDescendent(modalElement);
        /* istanbul ignore else */

        if (firstChild) {
          firstChild.focus();
        }
      } else {
        var allChildren = getAllTabbableDescendents(modalElement);
        /* istanbul ignore else */

        if (allChildren.length) {
          allChildren[allChildren.length - 1].focus();
        }
      }
    } else if ((e.key === 'Escape' || e.key === 'Esc') && onClose) {
      e.preventDefault();
      onClose(e);
    }
  };

  return {
    modalRef: modalRef,
    closing: closing,
    onModalKeyDown: onModalKeyDown
  };
}